/**
 * Promotion Dialog
 * 
 * Dialog สำหรับสร้าง/แก้ไขโปรโมชั่น
 * ออกแบบตาม UX/UI pattern ของระบบ React app
 */

import { useState, useEffect } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RadioGroup } from '@/components/ui/radio-group'
import { RadioItemCustom } from '@/components/ui/radio-item-custom'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/components/auth/auth-provider'
import { PromotionService, type Promotion, PromotionType, PromotionStatus, DepositConditionType, MinigameConditionType, BonusType, ConditionOperator, type Reward, PromotionCategory, PromotionCategoryLabels } from '@/domain/promotion'
import { Sparkles, Plus, Trash2, Loader2, Gift, FileText, Settings, Calendar, DollarSign, Info, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import moment from 'moment'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import { toDateTimeInputValue } from '@/lib/date-format'

// ============================================
// Validation Schema
// ============================================

const rewardSchema = z.object({
  bonus: z.number().min(0, 'ต้องมากกว่าหรือเท่ากับ 0'),
  weight: z.number().min(0, 'ต้องมากกว่าหรือเท่ากับ 0').max(100, 'ต้องไม่เกิน 100'),
})

const bonusConditionSchema = z.object({
  condition: z.nativeEnum(ConditionOperator),
  depositAmount: z.number().min(0, 'ต้องมากกว่าหรือเท่ากับ 0'),
  bonusPercent: z.number().min(0, 'ต้องมากกว่าหรือเท่ากับ 0').max(100, 'ต้องไม่เกิน 100'),
  maxBonus: z.number().min(0, 'ต้องมากกว่าหรือเท่ากับ 0'),
})

const playConditionSchema = z.object({
  condition: z.nativeEnum(ConditionOperator),
  depositAmount: z.number().min(0, 'ต้องมากกว่าหรือเท่ากับ 0'),
  playTimes: z.number().min(1, 'ต้องมากกว่าหรือเท่ากับ 1'),
})

const promotionSchema = z.object({
  name: z.string().min(1, 'กรุณากรอกชื่อโปรโมชั่น'),
  code: z.string().min(1, 'กรุณากรอกรหัสโปรโมชั่น'),
  description: z.string().optional(),
  promotion_type: z.nativeEnum(PromotionType),
  category: z.nativeEnum(PromotionCategory).optional(),
  deposit_condition_type: z.nativeEnum(DepositConditionType).optional(),
  minigame_condition_type: z.nativeEnum(MinigameConditionType).optional(),
  type: z.nativeEnum(BonusType).optional(),
  amount: z.number().optional(),
  deposit_min: z.number().optional(),
  withdraw_limit: z.number().optional(),
  deposit_turnover_bonus_x: z.number().optional(),
  accepted_max_perday: z.number().optional(),
  startDate: z.string().min(1, 'กรุณาเลือกวันที่เริ่มต้น'),
  endDate: z.string().min(1, 'กรุณาเลือกวันที่สิ้นสุด'),
  status: z.nativeEnum(PromotionStatus),
  autoSent: z.boolean().optional(),
  returnCredit: z.boolean().optional(),
  userOnlyFirstDeposit: z.boolean().optional(),
  rewards: z.array(rewardSchema).optional(),
  bonusConditions: z.array(bonusConditionSchema).optional(),
  playConditions: z.array(playConditionSchema).optional(),
  // Phase 1: New Fields
  max_usage_total: z.number().optional(),
  max_usage_per_member: z.number().optional(),
  budget_limit: z.number().optional(),
  priority: z.number().optional(),
  is_exclusive: z.boolean().optional(),
  member_groups: z.array(z.string()).optional(),
  image_url: z.string().optional(),
}).refine((data) => {
  if (data.startDate && data.endDate) {
    return new Date(data.endDate) > new Date(data.startDate)
  }
  return true
}, {
  message: 'วันที่สิ้นสุดต้องมากกว่าวันที่เริ่มต้น',
  path: ['endDate'],
}).refine((data) => {
  if (data.promotion_type === PromotionType.MINI_GAME && data.rewards) {
    const totalWeight = data.rewards.reduce((sum, r) => sum + (r.weight || 0), 0)
    return Math.abs(totalWeight - 100) < 0.1
  }
  return true
}, {
  message: 'น้ำหนักรวมต้องเท่ากับ 100%',
  path: ['rewards'],
})

type PromotionFormData = z.infer<typeof promotionSchema>

// ============================================
// Props
// ============================================

interface PromotionDialogProps {
  open: boolean
  onClose: (saved: boolean) => void
  promotion?: Promotion | null
  isEditMode?: boolean
}

// ============================================
// Component
// ============================================

export function PromotionDialog({ open, onClose, promotion, isEditMode = false }: PromotionDialogProps) {
  const { sessionToken } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [codeError, setCodeError] = useState<string | null>(null)
  const [isCheckingCode, setIsCheckingCode] = useState(false)

  // Default values
  const getDefaultValues = (): PromotionFormData => {
    if (promotion && isEditMode) {
      return {
        name: promotion.name,
        code: promotion.code,
        description: promotion.description || '',
        promotion_type: promotion.promotion_type,
        category: promotion.category || PromotionCategory.OTHER,
        deposit_condition_type: promotion.deposit_condition_type || DepositConditionType.FIRST_DEPOSIT,
        minigame_condition_type: promotion.minigame_condition_type || MinigameConditionType.FIRST_DEPOSIT,
        type: promotion.type || BonusType.PERCENTAGE,
        amount: promotion.amount || 0,
        deposit_min: promotion.deposit_min || 0,
        withdraw_limit: promotion.withdraw_limit || 100000,
        deposit_turnover_bonus_x: promotion.deposit_turnover_bonus_x || 5,
        accepted_max_perday: promotion.accepted_max_perday || 0,
        startDate: promotion.startDate ? moment(promotion.startDate).format('YYYY-MM-DDTHH:mm') : moment().format('YYYY-MM-DDTHH:mm'),
        endDate: promotion.endDate ? moment(promotion.endDate).format('YYYY-MM-DDTHH:mm') : moment().add(6, 'months').format('YYYY-MM-DDTHH:mm'),
        status: promotion.status || PromotionStatus.INACTIVE,
        autoSent: promotion.autoSent ?? true,
        returnCredit: promotion.returnCredit ?? true,
        userOnlyFirstDeposit: promotion.userOnlyFirstDeposit ?? false,
        rewards: promotion.rewards || [],
        bonusConditions: promotion.bonusConditions || [],
        playConditions: promotion.playConditions || [],
        // Phase 1: New Fields
        max_usage_total: promotion.max_usage_total || 0,
        max_usage_per_member: promotion.max_usage_per_member || 0,
        budget_limit: promotion.budget_limit || 0,
        priority: promotion.priority || 0,
        is_exclusive: promotion.is_exclusive || false,
        member_groups: promotion.member_groups || [],
        image_url: promotion.image_url || '',
      }
    }

    // New promotion defaults
    const endDate = moment().add(6, 'months')
    return {
      name: '',
      code: '',
      description: '',
      promotion_type: PromotionType.DEPOSIT_BONUS,
      category: PromotionCategory.NEW_MEMBER,
      deposit_condition_type: DepositConditionType.FIRST_DEPOSIT,
      minigame_condition_type: MinigameConditionType.FIRST_DEPOSIT,
      type: BonusType.PERCENTAGE,
      amount: 0,
      deposit_min: 0,
      withdraw_limit: 100000,
      deposit_turnover_bonus_x: 5,
      accepted_max_perday: 0,
      startDate: moment().format('YYYY-MM-DDTHH:mm'),
      endDate: endDate.format('YYYY-MM-DDTHH:mm'),
      status: PromotionStatus.INACTIVE,
      autoSent: true,
      returnCredit: true,
      userOnlyFirstDeposit: false,
      rewards: [],
      bonusConditions: [],
      playConditions: [],
      // Phase 1: New Fields
      max_usage_total: 0,
      max_usage_per_member: 0,
      budget_limit: 0,
      priority: 0,
      is_exclusive: false,
      member_groups: [],
      image_url: '',
    }
  }

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<PromotionFormData>({
    resolver: zodResolver(promotionSchema),
    defaultValues: getDefaultValues(),
  })

  // Watch values for conditional rendering
  const promotionType = watch('promotion_type')
  const depositConditionType = watch('deposit_condition_type')
  const minigameConditionType = watch('minigame_condition_type')

  // Form Arrays
  const { fields: rewardFields, append: appendReward, remove: removeReward } = useFieldArray({
    control,
    name: 'rewards',
  })

  const { fields: bonusConditionFields, append: appendBonusCondition, remove: removeBonusCondition } = useFieldArray({
    control,
    name: 'bonusConditions',
  })

  const { fields: playConditionFields, append: appendPlayCondition, remove: removePlayCondition } = useFieldArray({
    control,
    name: 'playConditions',
  })

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      reset(getDefaultValues())
      setCodeError(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Add default conditions/rewards when type changes (only for new promotions)
  useEffect(() => {
    if (!open || isEditMode) return
    
    // Add default bonus conditions for Type 1 (ฝากเงินตามเงื่อนไข) with first_deposit
    if (promotionType === PromotionType.DEPOSIT_BONUS && depositConditionType === DepositConditionType.FIRST_DEPOSIT) {
      if (bonusConditionFields.length === 0) {
        appendBonusCondition({ condition: ConditionOperator.GREATER_THAN, depositAmount: 100, bonusPercent: 5, maxBonus: 500 })
      }
    }
    
    // Add default play conditions and rewards for Type 4 (Mini Game) with first_deposit
    if (promotionType === PromotionType.MINI_GAME && minigameConditionType === MinigameConditionType.FIRST_DEPOSIT) {
      if (playConditionFields.length === 0) {
        appendPlayCondition({ condition: ConditionOperator.GREATER_THAN, depositAmount: 100, playTimes: 1 })
      }
      if (rewardFields.length === 0) {
        // Add default rewards
        const defaultRewards: Reward[] = [
          { bonus: 0, weight: 30 },
          { bonus: 5, weight: 25 },
          { bonus: 10, weight: 20 },
          { bonus: 20, weight: 15 },
          { bonus: 30, weight: 5 },
          { bonus: 50, weight: 3 },
          { bonus: 100, weight: 1.5 },
          { bonus: 500, weight: 0.4 },
          { bonus: 1000, weight: 0.09 },
          { bonus: 2000, weight: 0.01 },
        ]
        defaultRewards.forEach(r => appendReward(r))
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionType, depositConditionType, minigameConditionType])

  // Handle promotion type change
  useEffect(() => {
    if (promotionType === PromotionType.DEPOSIT_BONUS) {
      setValue('deposit_condition_type', DepositConditionType.FIRST_DEPOSIT)
      setValue('minigame_condition_type', undefined)
    } else if (promotionType === PromotionType.MINI_GAME) {
      setValue('deposit_condition_type', undefined)
      setValue('minigame_condition_type', MinigameConditionType.FIRST_DEPOSIT)
    }
  }, [promotionType, setValue])

  // Set default bonus type to 'fixed' when using normal deposit condition
  useEffect(() => {
    if (promotionType === PromotionType.DEPOSIT_BONUS && depositConditionType === DepositConditionType.NORMAL) {
      // โปรฝากมือ: ตั้งค่ารูปแบบโบนัสเป็น "จำนวนเงินคงที่" เป็น default
      setValue('type', BonusType.FIXED)
    }
  }, [promotionType, depositConditionType, setValue])

  // Generate code
  const generateCode = async () => {
    let code = ''
    let isUnique = false
    let attempts = 0
    const maxAttempts = 10

    while (!isUnique && attempts < maxAttempts) {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
      code = ''
      for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length))
      }

      try {
        const exists = await PromotionService.checkCodeExists(sessionToken!, code, isEditMode ? promotion?.objectId : undefined)
        if (!exists) {
          isUnique = true
        }
      } catch (err) {
        console.error('Error checking code:', err)
        break
      }

      attempts++
    }

    if (!isUnique) {
      const timestamp = Date.now().toString(36).toUpperCase()
      const random = Math.random().toString(36).substring(2, 6).toUpperCase()
      code = `${timestamp}${random}`.substring(0, 10)
    }

    setValue('code', code)
    setCodeError(null)
  }

  // Check code on change
  const handleCodeChange = async (value: string) => {
    if (!value) {
      setCodeError(null)
      return
    }

    const code = value.toUpperCase()
    setValue('code', code)

    if (isEditMode && promotion?.code === code) {
      setCodeError(null)
      return
    }

    setIsCheckingCode(true)
    try {
      const exists = await PromotionService.checkCodeExists(sessionToken!, code, isEditMode ? promotion?.objectId : undefined)
      if (exists) {
        setCodeError('รหัสนี้มีในระบบแล้ว')
      } else {
        setCodeError(null)
      }
    } catch (err) {
      console.error('Error checking code:', err)
    } finally {
      setIsCheckingCode(false)
    }
  }

  // Calculate total weight for rewards
  const totalWeight = rewardFields.reduce((sum, _, index) => {
    const weight = watch(`rewards.${index}.weight`) || 0
    return sum + Number(weight)
  }, 0)

  const isWeightValid = Math.abs(totalWeight - 100) < 0.1

  // Submit handler
  const onSubmit = async (data: PromotionFormData) => {
    if (promotionType === PromotionType.MINI_GAME && !isWeightValid) {
      alert(`น้ำหนักรวมต้องเท่ากับ 100% (ปัจจุบัน: ${totalWeight.toFixed(2)}%)`)
      return
    }

    if (!sessionToken) {
      alert('กรุณาเข้าสู่ระบบใหม่')
      return
    }

    setIsSubmitting(true)
    try {
      console.log('🎁 Promotion data to save:', data)
      
      // Prepare promotion data
      const promotionData: Partial<Promotion> = {
        name: data.name,
        code: data.code,
        description: data.description,
        promotion_type: data.promotion_type,
        category: data.category,
        deposit_condition_type: data.deposit_condition_type,
        minigame_condition_type: data.minigame_condition_type,
        type: data.type,
        amount: data.amount,
        deposit_min: data.deposit_min,
        withdraw_limit: data.withdraw_limit,
        deposit_turnover_bonus_x: data.deposit_turnover_bonus_x,
        accepted_max_perday: data.accepted_max_perday,
        startDate: data.startDate,
        endDate: data.endDate,
        status: data.status as PromotionStatus,
        autoSent: data.autoSent,
        returnCredit: data.returnCredit,
        userOnlyFirstDeposit: data.userOnlyFirstDeposit,
        rewards: data.rewards,
        bonusConditions: data.bonusConditions,
        playConditions: data.playConditions,
        // Phase 1: New Fields
        max_usage_total: data.max_usage_total,
        max_usage_per_member: data.max_usage_per_member,
        budget_limit: data.budget_limit,
        priority: data.priority,
        is_exclusive: data.is_exclusive,
        member_groups: data.member_groups,
        image_url: data.image_url,
      }

      if (isEditMode && promotion?.objectId) {
        // Update existing promotion
        await PromotionService.update(sessionToken, promotion.objectId, promotionData)
        console.log('✅ Promotion updated successfully')
      } else {
        // Create new promotion
        await PromotionService.create(sessionToken, promotionData)
        console.log('✅ Promotion created successfully')
      }

      onClose(true)
    } catch (err: any) {
      console.error('❌ Error saving promotion:', err)
      alert(`ไม่สามารถบันทึกโปรโมชั่นได้: ${err.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Helper functions for conditional rendering
  const shouldShowField = (fieldName: string): boolean => {
    if (promotionType === PromotionType.DEPOSIT_BONUS) {
      if (depositConditionType === DepositConditionType.FIRST_DEPOSIT || depositConditionType === DepositConditionType.CONDITION_MATCH) {
        const hiddenFields = ['type', 'amount', 'deposit_min', 'withdraw_limit']
        if (depositConditionType === DepositConditionType.FIRST_DEPOSIT) {
          hiddenFields.push('accepted_max_perday')
        }
        return !hiddenFields.includes(fieldName)
      }
    }

    if (promotionType === PromotionType.MINI_GAME) {
      if (minigameConditionType === MinigameConditionType.FIRST_DEPOSIT || minigameConditionType === MinigameConditionType.CONDITION_MATCH) {
        const hiddenFields = ['deposit_min']
        if (minigameConditionType === MinigameConditionType.FIRST_DEPOSIT) {
          hiddenFields.push('accepted_max_perday')
        }
        return !hiddenFields.includes(fieldName)
      }
    }

    return true
  }

  const shouldShowBonusConditions = (): boolean => {
    return promotionType === PromotionType.DEPOSIT_BONUS && 
           (depositConditionType === DepositConditionType.FIRST_DEPOSIT || depositConditionType === DepositConditionType.CONDITION_MATCH)
  }

  const shouldShowPlayConditions = (): boolean => {
    return promotionType === PromotionType.MINI_GAME && 
           (minigameConditionType === MinigameConditionType.FIRST_DEPOSIT || minigameConditionType === MinigameConditionType.CONDITION_MATCH)
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose(false)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <div className="sticky top-0 z-10 bg-background border-b px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/20">
                <Gift className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="text-lg">{isEditMode ? 'แก้ไขข้อมูลโปรโมชั่น' : 'เพิ่มโปรโมชั่นใหม่'}</span>
                <DialogDescription className="mt-1">
                  {isEditMode ? 'แก้ไขข้อมูลโปรโมชั่นในระบบ' : 'เพิ่มโปรโมชั่นใหม่เข้าสู่ระบบ'}
                </DialogDescription>
              </div>
            </DialogTitle>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} id="promotion-form" className="px-6 py-6 space-y-8">
          {/* Basic Information */}
          <div className="space-y-5">
            <h3 className="font-semibold text-base flex items-center gap-2 pb-3 border-b">
              <div className="w-6 h-6 rounded bg-blue-500/10 flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-blue-500" />
              </div>
              ข้อมูลพื้นฐาน
            </h3>
              
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  ชื่อโปรโมชั่น <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  {...register('name')}
                  placeholder="เช่น โปรโมชั่นต้อนรับสมาชิกใหม่"
                  className={cn(errors.name && 'border-red-500')}
                />
                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">
                  รหัสโปรโมชั่น <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="code"
                    {...register('code')}
                    onChange={(e) => handleCodeChange(e.target.value)}
                    className={cn("uppercase font-mono", errors.code && 'border-red-500')}
                    placeholder="เช่น A7K9M2X4"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={generateCode}
                    disabled={isCheckingCode}
                    title="สร้างรหัสอัตโนมัติ"
                  >
                    {isCheckingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  </Button>
                </div>
                {codeError && (
                  <p className="text-xs text-red-500">{codeError}</p>
                )}
                {!codeError && watch('code') && !isCheckingCode && (
                  <p className="text-xs text-green-600">✓ รหัสนี้ใช้ได้</p>
                )}
                {errors.code && (
                  <p className="text-xs text-red-500">{errors.code.message}</p>
                )}
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>
                กลุ่มโปรโมชัน <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="เลือกกลุ่มโปรโมชัน" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PromotionCategoryLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5" />
                จัดหมวดหมู่โปรโมชันเพื่อง่ายต่อการจัดการ
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">รายละเอียด</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="อธิบายรายละเอียดโปรโมชั่น"
                rows={3}
              />
            </div>

            {/* Promotion Type */}
            <div className="space-y-3">
              <Label>
                ประเภทโปรโมชั่น <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="promotion_type"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    value={String(field.value)}
                    onValueChange={(value) => field.onChange(Number(value))}
                    className="grid grid-cols-2 gap-3"
                  >
                    <RadioItemCustom
                      value={String(PromotionType.DEPOSIT_BONUS)}
                      label="ฝากเงินตามเงื่อนไข"
                      description="โบนัสสำหรับสมาชิกที่ฝากเงินตามเงื่อนไข"
                    />
                    <RadioItemCustom
                      value={String(PromotionType.MINI_GAME)}
                      label="Mini Game"
                      description="เกมสุ่มรางวัลโบนัส"
                    />
                  </RadioGroup>
                )}
              />
            </div>
          </div>

          {/* Bonus Settings */}
          <div className="space-y-5">
            <h3 className="font-semibold text-base flex items-center gap-2 pb-3 border-b">
              <div className="w-6 h-6 rounded bg-emerald-500/10 flex items-center justify-center">
                <Settings className="w-3.5 h-3.5 text-emerald-500" />
              </div>
              การตั้งค่าโบนัส
            </h3>
              
            {/* Deposit Condition Type (Type 1) */}
            {promotionType === PromotionType.DEPOSIT_BONUS && (
              <div className="space-y-2">
                <Label>
                  รูปแบบโปรโมชั่น <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="deposit_condition_type"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={DepositConditionType.FIRST_DEPOSIT}>
                            โปรลูกค้าใหม่ (อัตโนมัติ)
                          </SelectItem>
                          <SelectItem value={DepositConditionType.CONDITION_MATCH}>
                            โปรลูกค้าเก่า (อัตโนมัติ)
                          </SelectItem>
                          <SelectItem value={DepositConditionType.NORMAL}>
                            โปรฝากมือ
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5">
                        <Info className="w-3.5 h-3.5" />
                        {field.value === DepositConditionType.FIRST_DEPOSIT && 'โปรโมชั่นสำหรับลูกค้าใหม่ จำกัดเฉพาะยอดฝากครั้งแรก'}
                        {field.value === DepositConditionType.CONDITION_MATCH && 'โปรโมชั่นสำหรับลูกค้าเก่า เงื่อนไขพิเศษที่กำหนดเอง'}
                        {field.value === DepositConditionType.NORMAL && 'โปรโมชั่นสำหรับฝากมือ ไม่มีการส่งอัตโนมัติ'}
                      </p>
                    </>
                  )}
                />
              </div>
            )}

            {/* Mini Game Condition Type (Type 4) */}
            {promotionType === PromotionType.MINI_GAME && (
              <div className="space-y-2">
                <Label>
                  รูปแบบ Mini Game <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="minigame_condition_type"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={MinigameConditionType.FIRST_DEPOSIT}>
                            Mini Game ลูกค้าใหม่ (อัตโนมัติ)
                          </SelectItem>
                          <SelectItem value={MinigameConditionType.CONDITION_MATCH}>
                            Mini Game ลูกค้าเก่า (อัตโนมัติ)
                          </SelectItem>
                          <SelectItem value={MinigameConditionType.NORMAL}>
                            Mini ฝากมือ
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5">
                        <Info className="w-3.5 h-3.5" />
                        {field.value === MinigameConditionType.FIRST_DEPOSIT && 'Mini Game สำหรับลูกค้าใหม่ เล่นได้เฉพาะยอดฝากครั้งแรก'}
                        {field.value === MinigameConditionType.CONDITION_MATCH && 'Mini Game สำหรับลูกค้าเก่า เงื่อนไขพิเศษที่กำหนดเอง'}
                        {field.value === MinigameConditionType.NORMAL && 'Mini Game สำหรับฝากมือ ไม่มีการส่งอัตโนมัติ'}
                      </p>
                    </>
                  )}
                />
              </div>
            )}

            {/* Bonus Conditions (Type 1) */}
            {shouldShowBonusConditions() && (
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-500" />
                  เงื่อนไขโบนัส <span className="text-red-500">*</span>
                </Label>
                <div className="border rounded-xl p-4 space-y-3 bg-muted/20">
                  {bonusConditionFields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-2 flex-wrap p-3 bg-background rounded-lg border">
                      <Controller
                        name={`bonusConditions.${index}.condition`}
                        control={control}
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="w-28 h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={ConditionOperator.GREATER_THAN}>มากกว่า</SelectItem>
                              <SelectItem value={ConditionOperator.EQUAL_TO}>เท่ากับ</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <Input
                        type="number"
                        {...register(`bonusConditions.${index}.depositAmount`, { valueAsNumber: true })}
                        placeholder="100"
                        className="w-24 h-9"
                      />
                      <span className="text-sm text-muted-foreground">บาท →</span>
                      <Input
                        type="number"
                        {...register(`bonusConditions.${index}.bonusPercent`, { valueAsNumber: true })}
                        placeholder="5"
                        className="w-20 h-9"
                      />
                      <span className="text-sm text-muted-foreground">% →</span>
                      <span className="text-sm text-muted-foreground">ไม่เกิน</span>
                      <Input
                        type="number"
                        {...register(`bonusConditions.${index}.maxBonus`, { valueAsNumber: true })}
                        placeholder="500"
                        className="w-24 h-9"
                      />
                      <span className="text-sm text-muted-foreground">บาท</span>
                      {bonusConditionFields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-destructive hover:text-destructive"
                          onClick={() => removeBonusCondition(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendBonusCondition({ condition: ConditionOperator.GREATER_THAN, depositAmount: 100, bonusPercent: 5, maxBonus: 500 })}
                  >
                    <Plus className="w-4 h-4 mr-1.5" />
                    เพิ่มเงื่อนไข
                  </Button>
                </div>
              </div>
            )}

            {/* Play Conditions (Type 4) */}
            {shouldShowPlayConditions() && (
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  เงื่อนไขการเล่น Mini Game <span className="text-red-500">*</span>
                </Label>
                <div className="border rounded-xl p-4 space-y-3 bg-muted/20">
                  {playConditionFields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-2 flex-wrap p-3 bg-background rounded-lg border">
                      <Controller
                        name={`playConditions.${index}.condition`}
                        control={control}
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="w-28 h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={ConditionOperator.GREATER_THAN}>มากกว่า</SelectItem>
                              <SelectItem value={ConditionOperator.EQUAL_TO}>เท่ากับ</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <Input
                        type="number"
                        {...register(`playConditions.${index}.depositAmount`, { valueAsNumber: true })}
                        placeholder="100"
                        className="w-24 h-9"
                      />
                      <span className="text-sm text-muted-foreground">บาท → เล่นได้</span>
                      <Input
                        type="number"
                        {...register(`playConditions.${index}.playTimes`, { valueAsNumber: true })}
                        placeholder="1"
                        className="w-20 h-9"
                      />
                      <span className="text-sm text-muted-foreground">ครั้ง</span>
                      {playConditionFields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-destructive hover:text-destructive"
                          onClick={() => removePlayCondition(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendPlayCondition({ condition: ConditionOperator.GREATER_THAN, depositAmount: 100, playTimes: 1 })}
                  >
                    <Plus className="w-4 h-4 mr-1.5" />
                    เพิ่มเงื่อนไข
                  </Button>
                </div>
              </div>
            )}

            {/* Normal Bonus Fields (not using conditions) */}
            {promotionType === PromotionType.DEPOSIT_BONUS && !shouldShowBonusConditions() && (
              <div className="grid grid-cols-2 gap-4">
                {shouldShowField('type') && (
                  <div className="space-y-2">
                    <Label>รูปแบบโบนัส</Label>
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={BonusType.FIXED}>จำนวนเงินคงที่ (บาท)</SelectItem>
                            <SelectItem value={BonusType.PERCENTAGE}>เปอร์เซ็นต์ (%)</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                )}

                {shouldShowField('amount') && (
                  <div className="space-y-2">
                    <Label>จำนวนโบนัส</Label>
                    <Input
                      type="number"
                      {...register('amount', { valueAsNumber: true })}
                      placeholder="0"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Mini Game Rewards */}
            {promotionType === PromotionType.MINI_GAME && (
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Gift className="w-4 h-4 text-pink-500" />
                  รางวัล Mini Game <span className="text-red-500">*</span>
                </Label>
                
                <div className="border rounded-xl overflow-hidden bg-muted/20">
                  <div className="grid grid-cols-3 gap-4 p-4 text-sm font-medium text-muted-foreground bg-muted/50">
                    <span>โบนัส (บาท)</span>
                    <span>น้ำหนัก (%)</span>
                    <span></span>
                  </div>
                  
                  <div className="p-4 space-y-3">
                    {rewardFields.map((field, index) => (
                      <div key={field.id} className="grid grid-cols-3 gap-4 items-center">
                        <Input
                          type="number"
                          {...register(`rewards.${index}.bonus`, { valueAsNumber: true })}
                          placeholder="0"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          {...register(`rewards.${index}.weight`, { valueAsNumber: true })}
                          placeholder="0"
                        />
                        {rewardFields.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => removeReward(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border-t bg-muted/50">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => appendReward({ bonus: 0, weight: 0 })}
                    >
                      <Plus className="w-4 h-4 mr-1.5" />
                      เพิ่มรางวัล
                    </Button>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">น้ำหนักรวม:</span>
                      <strong
                        className={cn(
                          "text-base",
                          isWeightValid && "text-green-600",
                          !isWeightValid && totalWeight > 100.1 && "text-red-600",
                          !isWeightValid && totalWeight <= 100.1 && "text-amber-600"
                        )}
                      >
                        {totalWeight.toFixed(2)}%
                      </strong>
                      {isWeightValid && (
                        <Badge variant="default" className="bg-green-500">
                          ✓ ถูกต้อง
                        </Badge>
                      )}
                      {!isWeightValid && totalWeight > 100.1 && (
                        <Badge variant="destructive">
                          ⚠️ เกิน 100%
                        </Badge>
                      )}
                      {!isWeightValid && totalWeight <= 100.1 && (
                        <Badge variant="outline" className="border-amber-500 text-amber-600">
                          ⚠️ ต้องเท่ากับ 100%
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Other Fields */}
            <div className="grid grid-cols-2 gap-4">
              {/* deposit_min: ซ่อนเมื่อ Type 6 หรือ Mini Game ใช้ shouldShowField */}
              {promotionType !== PromotionType.CREDIT_GIFT && shouldShowField('deposit_min') && (
                <div className="space-y-2">
                  <Label>ฝากขั้นต่ำ (บาท) <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    {...register('deposit_min', { valueAsNumber: true })}
                    placeholder="0"
                  />
                </div>
              )}

              {/* withdraw_limit: ซ่อนเมื่อ Type 4 (Mini Game) */}
              {promotionType !== PromotionType.MINI_GAME && shouldShowField('withdraw_limit') && (
                <div className="space-y-2">
                  <Label>ถอนได้สูงสุด (บาท) <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    {...register('withdraw_limit', { valueAsNumber: true })}
                    placeholder="100000"
                  />
                </div>
              )}

              {/* deposit_turnover_bonus_x: ซ่อนเมื่อ Type 4 (Mini Game) */}
              {promotionType !== PromotionType.MINI_GAME && shouldShowField('deposit_turnover_bonus_x') && (
                <div className="space-y-2">
                  <Label>ทำเทิร์น (เท่า) <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    {...register('deposit_turnover_bonus_x', { valueAsNumber: true })}
                    placeholder="5"
                  />
                </div>
              )}

              {/* accepted_max_perday: ซ่อนเมื่อ Type 6 หรือตาม shouldShowField */}
              {promotionType !== PromotionType.CREDIT_GIFT && shouldShowField('accepted_max_perday') && (
                <div className="space-y-2">
                  <Label>รับได้สูงสุด (ครั้ง/วัน)</Label>
                  <Input
                    type="number"
                    {...register('accepted_max_perday', { valueAsNumber: true })}
                    placeholder="0"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Period & Status */}
          <div className="space-y-5">
            <h3 className="font-semibold text-base flex items-center gap-2 pb-3 border-b">
              <div className="w-6 h-6 rounded bg-orange-500/10 flex items-center justify-center">
                <Calendar className="w-3.5 h-3.5 text-orange-500" />
              </div>
              ระยะเวลาและสถานะ
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">
                  วันที่เริ่มต้น <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      selected={field.value ? new Date(field.value) : null}
                      onChange={(date) => {
                        field.onChange(date ? toDateTimeInputValue(date) : '');
                      }}
                      placeholder="เลือกวันที่และเวลาเริ่มต้น"
                    />
                  )}
                />
                {errors.startDate && (
                  <p className="text-xs text-red-500">{errors.startDate.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">
                  วันที่สิ้นสุด <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      selected={field.value ? new Date(field.value) : null}
                      onChange={(date) => {
                        field.onChange(date ? toDateTimeInputValue(date) : '');
                      }}
                      placeholder="เลือกวันที่และเวลาสิ้นสุด"
                    />
                  )}
                />
                {errors.endDate && (
                  <p className="text-xs text-red-500">{errors.endDate.message}</p>
                )}
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="status">
                  สถานะ <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={PromotionStatus.ACTIVE}>เปิดใช้งาน</SelectItem>
                        <SelectItem value={PromotionStatus.INACTIVE}>ปิดใช้งาน</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Targeting & Priority (Phase 1) */}
          <div className="space-y-5">
            <h3 className="font-semibold text-base flex items-center gap-2 pb-3 border-b">
              <div className="w-6 h-6 rounded bg-violet-500/10 flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-violet-500" />
              </div>
              กลุ่มเป้าหมายและลำดับความสำคัญ
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ลำดับความสำคัญ</Label>
                <Input
                  type="number"
                  {...register('priority', { valueAsNumber: true })}
                  placeholder="0"
                  min={0}
                  max={100}
                />
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5" />
                  ยิ่งสูงยิ่งใช้ก่อน (0 = ไม่จำกัด)
                </p>
              </div>

              <div className="space-y-2">
                <Label>ห้ามใช้ร่วมกับโปรอื่น</Label>
                <Controller
                  name="is_exclusive"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center gap-3 h-10">
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                      <span className="text-sm text-muted-foreground">
                        {field.value ? 'เปิดใช้งาน (Exclusive)' : 'ปิดใช้งาน'}
                      </span>
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>URL รูปโปรโมชั่น</Label>
              <Input
                {...register('image_url')}
                placeholder="https://example.com/promo-banner.jpg"
              />
              <p className="text-xs text-muted-foreground">
                ใส่ URL รูปภาพสำหรับแสดงใน frontend (optional)
              </p>
            </div>
          </div>

          {/* Usage Limits (Phase 1) */}
          <div className="space-y-5">
            <h3 className="font-semibold text-base flex items-center gap-2 pb-3 border-b">
              <div className="w-6 h-6 rounded bg-red-500/10 flex items-center justify-center">
                <AlertCircle className="w-3.5 h-3.5 text-red-500" />
              </div>
              จำกัดการใช้งาน
            </h3>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>จำกัดรับโปรทั้งหมด</Label>
                <Input
                  type="number"
                  {...register('max_usage_total', { valueAsNumber: true })}
                  placeholder="0"
                  min={0}
                />
                <p className="text-xs text-muted-foreground">0 = ไม่จำกัด</p>
              </div>

              <div className="space-y-2">
                <Label>จำกัดต่อสมาชิก</Label>
                <Input
                  type="number"
                  {...register('max_usage_per_member', { valueAsNumber: true })}
                  placeholder="0"
                  min={0}
                />
                <p className="text-xs text-muted-foreground">0 = ไม่จำกัด</p>
              </div>

              <div className="space-y-2">
                <Label>งบประมาณรวม (บาท)</Label>
                <Input
                  type="number"
                  {...register('budget_limit', { valueAsNumber: true })}
                  placeholder="0"
                  min={0}
                />
                <p className="text-xs text-muted-foreground">0 = ไม่จำกัด</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 mt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={isSubmitting}
              size="lg"
            >
              ยกเลิก
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              size="lg"
              className="min-w-32"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditMode ? 'บันทึกการแก้ไข' : 'สร้างโปรโมชั่น'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
